import Phaser from 'phaser';
import { EntityManager } from '../entities/EntityManager';
import { Tower } from '../entities/Tower';
import { Player } from '../entities/Player';
import { TreeSlot } from '../entities/TreeSlot';
import { InputRouter, PlayerId } from './InputRouter';
import { SessionManager } from './SessionManager';
import { DropSystem } from './DropSystem';
import { COLORS, GAME } from '../constants';

// ============================================
// Tower Interaction System
// Handles sell, upgrade, and passive leveling when player stands on tower
// ============================================

// Passive upgrade config - time increases with tower level
const PASSIVE_UPGRADE_CONFIG = {
  baseTimeSeconds: 22.5,    // Base time for level 1 -> 2 (50% longer than original 15s)
  timeMultiplierPerLevel: 1.5,  // Each level takes 1.5x longer
  maxLevel: 6,              // Black = 5, Rainbow (dance-only) = 6
  rainbowTimeMultiplier: 10,    // Level 5->6 takes 10x longer than 4->5
  bounceAmount: 3,          // Pixels to bounce player
  bounceSpeed: 200,         // Ms per bounce cycle
  interactionRadius: 35,    // Player must be within this distance of tower center
};

// Manual upgrade costs (gems) per level - from PixelJunk Monsters guide
// Sequence: Green→Yellow→Red→Purple→Black (Rainbow cannot be purchased)
const UPGRADE_GEM_COSTS: Record<number, number> = {
  1: 1,   // Green → Yellow (1 gem)
  2: 1,   // Yellow → Red (1 gem)
  3: 2,   // Red → Purple (2 gems)
  4: 3,   // Purple → Black (3 gems)
  // Level 5 → 6 (Rainbow) is dance-only, no gem cost
};

interface TowerInteraction {
  tower: Tower;
  playerId: PlayerId;
  upgradeTime: number;      // Total time needed in ms
  bouncePhase: number;      // For bounce animation
  // Note: upgrade progress is now stored on the Tower itself (tower.getUpgradeProgress())
}

class TowerInteractionSystemManager {
  private scene: Phaser.Scene | null = null;
  
  // Active interactions per player
  private interactions: Map<PlayerId, TowerInteraction> = new Map();
  
  // Note: upgrade progress is now stored on the Tower itself (tower.getUpgradeProgress())
  // This allows kill-based and player-based progress to share the same pool
  
  // Range circles per tower (shown when player is on tower)
  private rangeCircles: Map<string, Phaser.GameObjects.Arc> = new Map();
  
  // UI elements per tower
  private towerUI: Map<string, {
    container: Phaser.GameObjects.Container;
    progressBg: Phaser.GameObjects.Rectangle;
    progressBar: Phaser.GameObjects.Rectangle;
    progressText: Phaser.GameObjects.Text;
    sellButton: Phaser.GameObjects.Container;
    upgradeButton: Phaser.GameObjects.Container;
  }> = new Map();

  // Callbacks
  public onTowerSold?: (tower: Tower, refund: number) => void;
  public onTowerUpgraded?: (tower: Tower, newLevel: number) => void;

  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.interactions.clear();
    this.rangeCircles.forEach(c => c.destroy());
    this.rangeCircles.clear();
    this.towerUI.forEach(ui => ui.container.destroy());
    this.towerUI.clear();
    console.log('[TowerInteractionSystem] Initialized');
  }

  shutdown(): void {
    this.interactions.clear();
    this.rangeCircles.forEach(c => c.destroy());
    this.rangeCircles.clear();
    this.towerUI.forEach(ui => ui.container.destroy());
    this.towerUI.clear();
    this.scene = null;
  }

  update(delta: number): void {
    if (!this.scene) return;

    const players = EntityManager.getByType<Player>('player');
    const towers = EntityManager.getByType<Tower>('tower');

    // Check each player for tower proximity
    for (const player of players) {
      const playerId = player.playerId;
      let nearestTower: Tower | null = null;
      let nearestDist = PASSIVE_UPGRADE_CONFIG.interactionRadius;

      // Find nearest tower within interaction radius (must be built - no dancing/upgrade during construction)
      for (const tower of towers) {
        if (tower.destroyed || tower.isBuilding()) continue;
        const dist = player.distanceTo(tower);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestTower = tower;
        }
      }

      const currentInteraction = this.interactions.get(playerId);

      if (nearestTower) {
        // Player is near a tower
        if (!currentInteraction || currentInteraction.tower !== nearestTower) {
          // Start new interaction
          this.startInteraction(playerId, nearestTower, player);
        } else {
          // Continue existing interaction
          this.updateInteraction(playerId, currentInteraction, delta, player);
        }
      } else if (currentInteraction) {
        // Player left tower area
        this.endInteraction(playerId);
      }
    }

    // Handle input for sell/upgrade
    this.handleInput();

    // Update UI for all active interactions
    this.updateAllUI();

    // Clean up UI for towers that no longer exist or aren't being interacted with
    this.cleanupUI(towers);
  }

  private startInteraction(playerId: PlayerId, tower: Tower, player: Player): void {
    const level = tower.getLevel();
    const upgradeTime = this.getUpgradeTime(level);

    // Progress is stored on the tower itself, so no need to track separately
    this.interactions.set(playerId, {
      tower,
      playerId,
      upgradeTime,
      bouncePhase: 0,
    });

    // Start upgrade dancing animation on player
    player.setUpgradeDancing(true);

    // Create UI for this tower if not exists
    this.createTowerUI(tower);

    // Show the UI
    const ui = this.towerUI.get(tower.id);
    if (ui) {
      ui.container.setVisible(true);
    }

    // Show range circle
    this.showRangeCircle(tower);
  }

  private updateInteraction(
    playerId: PlayerId,
    interaction: TowerInteraction,
    delta: number,
    player: Player
  ): void {
    const tower = interaction.tower;
    const level = tower.getLevel();

    // Check if tower can still be upgraded
    if (level >= PASSIVE_UPGRADE_CONFIG.maxLevel) {
      // Max level - stop dancing and just show UI
      player.setBounceOffset(0);
      player.setUpgradeDancing(false);
      return;
    }

    // Note: Player handles its own BPM-synced dance animation via updateUpgradeDance()
    // We just need to track time for upgrade progress calculation
    interaction.bouncePhase += delta;

    // Progress the passive upgrade (stored on tower, shared with kill-based progress)
    const progressAmount = delta / interaction.upgradeTime;
    const leveledUp = tower.addUpgradeProgress(progressAmount);

    if (leveledUp) {
      // Upgrade complete!
      this.onPassiveUpgradeComplete(interaction, player);
    }
  }

  private onPassiveUpgradeComplete(interaction: TowerInteraction, player: Player): void {
    const tower = interaction.tower;
    // Note: tower.addUpgradeProgress() already called tower.upgrade() and reset progress

    // Visual feedback
    this.showUpgradeEffect(tower);

    // Callback
    this.onTowerUpgraded?.(tower, tower.getLevel());

    console.log(`[TowerInteraction] Passive upgrade complete: ${tower.towerType} now Lv${tower.getLevel()}`);

    // If tower reached max level, stop dancing
    if (tower.getLevel() >= PASSIVE_UPGRADE_CONFIG.maxLevel) {
      player.setBounceOffset(0);
      player.setUpgradeDancing(false);
    } else {
      // Update upgrade time for next level
      interaction.upgradeTime = this.getUpgradeTime(tower.getLevel());
    }

    // Update range circle for new range
    this.showRangeCircle(tower);
  }

  private endInteraction(playerId: PlayerId): void {
    const interaction = this.interactions.get(playerId);
    if (interaction) {
      const tower = interaction.tower;
      // Progress is stored on the tower itself, no need to save separately

      // Reset player bounce and stop upgrade dancing
      const players = EntityManager.getByType<Player>('player');
      const player = players.find(p => p.playerId === playerId);
      if (player) {
        player.setBounceOffset(0);
        player.setUpgradeDancing(false);
      }

      // Hide UI and range if no other player is interacting with this tower
      const otherInteracting = Array.from(this.interactions.values())
        .some(i => i.tower === tower && i.playerId !== playerId);
      
      if (!otherInteracting) {
        const ui = this.towerUI.get(tower.id);
        if (ui) {
          ui.container.setVisible(false);
        }
        // Hide range circle
        this.hideRangeCircle(tower);
      }
    }

    this.interactions.delete(playerId);
  }

  private handleInput(): void {
    // Check for sell input (Q key or specific button)
    const p1Sell = InputRouter.wasActionJustPressed('P1', 'SELL');
    const p2Sell = InputRouter.wasActionJustPressed('P2', 'SELL');

    if (p1Sell) this.trySellTower('P1');
    if (p2Sell) this.trySellTower('P2');

    // Check for instant upgrade input (U key or specific button)
    const p1Upgrade = InputRouter.wasActionJustPressed('P1', 'UPGRADE');
    const p2Upgrade = InputRouter.wasActionJustPressed('P2', 'UPGRADE');

    if (p1Upgrade) this.tryInstantUpgrade('P1');
    if (p2Upgrade) this.tryInstantUpgrade('P2');
  }

  trySellTower(playerId: PlayerId): void {
    const interaction = this.interactions.get(playerId);
    if (!interaction) return;

    const tower = interaction.tower;
    if (tower.isBuilding()) return;
    // 50% of original build cost back
    const sellValue = Math.floor(tower.getCost() / 2);
    const level = tower.getLevel();
    // Gems back by level: purple (4) = 1, black (5) = 2, rainbow (6) = 3
    const gemCount = level === 4 ? 1 : level === 5 ? 2 : level === 6 ? 3 : 0;

    const x = tower.x;
    const y = tower.y;

    // Coins and gems splash out as pickups (like monster/tree drops)
    DropSystem.spawnTowerSellDrops(x, y, sellValue, gemCount);

    // Visual feedback
    this.showSellEffect(tower, sellValue);

    // Callback
    this.onTowerSold?.(tower, sellValue);

    console.log(`[TowerInteraction] Sold ${tower.towerType} for ${sellValue} coins, ${gemCount} gems (Lv${level})`);

    // Clear the tree slot
    const slots = EntityManager.getByType('treeSlot') as TreeSlot[];
    for (const slot of slots) {
      if (slot.getTowerId?.() === tower.id) {
        slot.clearOccupied?.();
        break;
      }
    }

    // End interaction and destroy tower
    this.endInteraction(playerId);
    tower.destroy();
  }

  tryInstantUpgrade(playerId: PlayerId): void {
    const interaction = this.interactions.get(playerId);
    if (!interaction) return;

    const tower = interaction.tower;
    if (tower.isBuilding()) return;

    const level = tower.getLevel();

    if (level >= PASSIVE_UPGRADE_CONFIG.maxLevel) {
      this.showMessage(tower.x, tower.y, 'MAX LEVEL', '#ffff00');
      return;
    }

    // Rainbow (level 6) cannot be purchased with gems — dance only
    if (level >= 5) {
      this.showMessage(tower.x, tower.y, 'Rainbow: dance only!', '#ff69b4');
      return;
    }

    const cost = UPGRADE_GEM_COSTS[level] || 50;

    if (!SessionManager.canAffordGems(cost)) {
      this.showMessage(tower.x, tower.y, `Need ${cost} gems!`, '#ff4444');
      return;
    }

    // Spend gems
    SessionManager.spendGems(cost, playerId);

    // Upgrade tower and reset its progress
    tower.upgrade();
    tower.setUpgradeProgress(0);

    // Visual feedback
    this.showUpgradeEffect(tower);

    // Callback
    this.onTowerUpgraded?.(tower, tower.getLevel());

    console.log(`[TowerInteraction] Instant upgrade: ${tower.towerType} -> Lv${tower.getLevel()} (cost: ${cost} gems)`);

    // Update upgrade time for next level
    interaction.upgradeTime = this.getUpgradeTime(tower.getLevel());

    // Update range circle for new range
    this.showRangeCircle(tower);
  }

  private getUpgradeTime(currentLevel: number): number {
    // Level 5->6 (rainbow) is 10x longer than 4->5, dance-only
    if (currentLevel === 5) {
      const level4To5Ms = PASSIVE_UPGRADE_CONFIG.baseTimeSeconds
        * Math.pow(PASSIVE_UPGRADE_CONFIG.timeMultiplierPerLevel, 3) * 1000;
      return level4To5Ms * PASSIVE_UPGRADE_CONFIG.rainbowTimeMultiplier;
    }
    const multiplier = Math.pow(PASSIVE_UPGRADE_CONFIG.timeMultiplierPerLevel, currentLevel - 1);
    return PASSIVE_UPGRADE_CONFIG.baseTimeSeconds * multiplier * 1000; // ms
  }

  // ============================================
  // UI Management
  // ============================================

  private createTowerUI(tower: Tower): void {
    if (!this.scene || this.towerUI.has(tower.id)) return;

    const x = tower.x;
    const y = tower.y - GAME.TOWER_SIZE / 2 - 40;

    const container = this.scene.add.container(x, y);
    container.setDepth(200);

    // Progress bar background
    const progressBg = this.scene.add.rectangle(0, 0, 60, 8, 0x333333);
    progressBg.setStrokeStyle(1, 0xffffff);

    // Progress bar fill
    const progressBar = this.scene.add.rectangle(-29, 0, 0, 6, COLORS.ACCENT);
    progressBar.setOrigin(0, 0.5);

    // Progress text
    const progressText = this.scene.add.text(0, -12, '', {
      fontSize: '10px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    progressText.setOrigin(0.5);

    // Sell button
    const sellButton = this.createButton(-35, 20, 'Q:Sell', 0x884444, () => {
      // Find which player is interacting
      for (const [pid, interaction] of this.interactions) {
        if (interaction.tower === tower) {
          this.trySellTower(pid);
          break;
        }
      }
    });

    // Upgrade button
    const upgradeButton = this.createButton(35, 20, 'U:Upgrade', 0x448844, () => {
      for (const [pid, interaction] of this.interactions) {
        if (interaction.tower === tower) {
          this.tryInstantUpgrade(pid);
          break;
        }
      }
    });

    container.add([progressBg, progressBar, progressText, sellButton, upgradeButton]);
    container.setVisible(false);

    this.towerUI.set(tower.id, {
      container,
      progressBg,
      progressBar,
      progressText,
      sellButton,
      upgradeButton,
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void
  ): Phaser.GameObjects.Container {
    if (!this.scene) return new Phaser.GameObjects.Container(this.scene!, x, y);

    const container = this.scene.add.container(x, y);

    const bg = this.scene.add.rectangle(0, 0, 55, 18, color);
    bg.setStrokeStyle(1, 0xffffff);
    bg.setInteractive({ useHandCursor: true });

    const text = this.scene.add.text(0, 0, label, {
      fontSize: '9px',
      color: '#ffffff',
    });
    text.setOrigin(0.5);

    bg.on('pointerdown', onClick);
    bg.on('pointerover', () => bg.setFillStyle(Phaser.Display.Color.GetColor(
      Math.min(255, ((color >> 16) & 0xff) + 40),
      Math.min(255, ((color >> 8) & 0xff) + 40),
      Math.min(255, (color & 0xff) + 40)
    )));
    bg.on('pointerout', () => bg.setFillStyle(color));

    container.add([bg, text]);
    return container;
  }

  private updateAllUI(): void {
    // Count dancers per tower: display updates reflexively (e.g. 1 player 120s → 2nd joins → 60s; 1 leaves → back up)
    const dancerCountByTower = new Map<string, number>();
    for (const interaction of this.interactions.values()) {
      const id = interaction.tower.id;
      dancerCountByTower.set(id, (dancerCountByTower.get(id) ?? 0) + 1);
    }

    for (const [_playerId, interaction] of this.interactions) {
      const ui = this.towerUI.get(interaction.tower.id);
      if (!ui) continue;

      const tower = interaction.tower;
      const level = tower.getLevel();
      const isMaxLevel = level >= PASSIVE_UPGRADE_CONFIG.maxLevel;
      const dancerCount = Math.max(1, dancerCountByTower.get(tower.id) ?? 1);

      // Update progress bar (progress is stored on tower, shared with kill-based upgrades)
      const progress = isMaxLevel ? 1 : tower.getUpgradeProgress();
      ui.progressBar.setSize(58 * progress, 6);
      ui.progressBar.setFillStyle(isMaxLevel ? 0xffff00 : COLORS.ACCENT);

      // Display effective time left: 1 player = full time (e.g. 120s), 2 players = half (60s); when 1 steps off, time doubles back
      if (isMaxLevel) {
        ui.progressText.setText('MAX LEVEL');
        ui.progressText.setColor('#ffff00');
      } else {
        const timeLeftReal = (1 - progress) * interaction.upgradeTime / 1000;
        const timeLeftDisplay = Math.ceil(timeLeftReal / dancerCount);
        ui.progressText.setText(`Lv${level}→${level + 1} (${timeLeftDisplay}s)`);
        ui.progressText.setColor('#ffffff');
      }

      // Update sell button text with value
      const sellValue = Math.floor(tower.getCost() / 2);
      const sellText = (ui.sellButton.list[1] as Phaser.GameObjects.Text);
      sellText.setText(`Q:${sellValue}c`);

      // Update upgrade button with cost
      const upgradeCost = UPGRADE_GEM_COSTS[level] || 0;
      const upgradeText = (ui.upgradeButton.list[1] as Phaser.GameObjects.Text);
      if (isMaxLevel) {
        upgradeText.setText('MAX');
        (ui.upgradeButton.list[0] as Phaser.GameObjects.Rectangle).setFillStyle(0x444444);
      } else {
        upgradeText.setText(`U:${upgradeCost}g`);
        const canAfford = SessionManager.canAffordGems(upgradeCost);
        (ui.upgradeButton.list[0] as Phaser.GameObjects.Rectangle).setFillStyle(canAfford ? 0x448844 : 0x664444);
      }

      // Position container above tower
      ui.container.setPosition(tower.x, tower.y - GAME.TOWER_SIZE / 2 - 40);
    }
  }

  private cleanupUI(activeTowers: Tower[]): void {
    const activeTowerIds = new Set(activeTowers.map(t => t.id));

    for (const [towerId, ui] of this.towerUI) {
      if (!activeTowerIds.has(towerId)) {
        ui.container.destroy();
        this.towerUI.delete(towerId);
      }
    }
  }

  // ============================================
  // Range Circle
  // ============================================

  private showRangeCircle(tower: Tower): void {
    if (!this.scene) return;

    // Remove existing circle for this tower
    this.hideRangeCircle(tower);

    // Create new range circle
    const range = tower.getRange();
    const circle = this.scene.add.circle(tower.x, tower.y, range);
    circle.setStrokeStyle(2, 0xffffff, 0.4);
    circle.setFillStyle(0xffffff, 0.08);
    circle.setDepth(15); // Below towers but above ground

    this.rangeCircles.set(tower.id, circle);
  }

  private hideRangeCircle(tower: Tower): void {
    const circle = this.rangeCircles.get(tower.id);
    if (circle) {
      circle.destroy();
      this.rangeCircles.delete(tower.id);
    }
  }

  // ============================================
  // Visual Effects
  // ============================================

  private showUpgradeEffect(tower: Tower): void {
    if (!this.scene) return;

    // Burst of particles
    const circle = this.scene.add.circle(tower.x, tower.y, 10, COLORS.ACCENT, 0.8);
    circle.setDepth(150);

    this.scene.tweens.add({
      targets: circle,
      radius: 50,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => circle.destroy(),
    });

    // Level up text
    this.showMessage(tower.x, tower.y - 30, `LEVEL ${tower.getLevel()}!`, '#00ff00');
  }

  private showSellEffect(tower: Tower, value: number): void {
    if (!this.scene) return;

    // Coins flying out
    this.showMessage(tower.x, tower.y, `+${value} coins`, '#ffdd44');

    // Poof effect
    const circle = this.scene.add.circle(tower.x, tower.y, 20, 0x888888, 0.6);
    circle.setDepth(150);

    this.scene.tweens.add({
      targets: circle,
      radius: 40,
      alpha: 0,
      duration: 300,
      onComplete: () => circle.destroy(),
    });
  }

  private showMessage(x: number, y: number, text: string, color: string): void {
    if (!this.scene) return;

    const msg = this.scene.add.text(x, y, text, {
      fontSize: '14px',
      color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    msg.setOrigin(0.5);
    msg.setDepth(200);

    this.scene.tweens.add({
      targets: msg,
      y: y - 40,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => msg.destroy(),
    });
  }

  // ============================================
  // Getters
  // ============================================

  getInteraction(playerId: PlayerId): TowerInteraction | undefined {
    return this.interactions.get(playerId);
  }

  isPlayerInteracting(playerId: PlayerId): boolean {
    return this.interactions.has(playerId);
  }
}

// Singleton instance
export const TowerInteractionSystem = new TowerInteractionSystemManager();
